import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { OfflineTrackerAPIModel, PaginatedAPI, TaskTypeAPIModel } from 'src/app/models/api.model'
import { Observable } from 'rxjs'
import {
  ENTITY_ID_WITH_FILTER,
  OT_DISPLAY_ID,
  OT_EE_ID,
  OT_EE_NAME,
  OT_UNIQUE_MEMBER_ID,
  OT_RECEIVED_DATE,
  LAST_UPDATED_BY,
  OT_LAST_UPDATED_ON
} from 'src/app/constants/ui.columns'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { AppState } from 'src/app/store/state/app.state'
import { Store } from '@ngrx/store'
import { DocumentService } from 'src/app/services/common/document.service'
import { SAVE_SUMMARY_TABLE_SEARCH_PARAMS } from 'src/app/store/action/common/common.action'
import { OFFLINE_TRACKER } from 'src/app/constants/field-id-constants'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import {
  getOptionsFromState,
  getStatusMapFromTaskTypes,
  getUIStatusMappingFromState
} from 'src/app/store/selector/initialize.selector'
import { LOAD_OFFLINE_TRACKER_SUMMARY_DETAILS } from 'src/app/store/action/offline-tracker.action'
import { getSummaryList } from 'src/app/store/selector/offline-tracker.selector'
import { Column, ColumnType, FieldType } from 'src/app/models/model'
import { SummaryTableService } from 'src/app/services/common/summary-table.service'

@Component({
  selector: 'app-offline-tracker-summary',
  templateUrl: './offline-tracker-summary.component.html',
  styleUrls: ['./offline-tracker-summary.component.scss']
})
export class OfflineTrackerSummaryComponent implements OnInit {
  fromDate: any
  toDate: any
  summaryOptions: SummaryDataTableOptions
  summaryData: SummaryDataTableData<OfflineTrackerAPIModel>
  offlineTrackerSummaryAPIData$: Observable<PaginatedAPI<OfflineTrackerAPIModel>>
  private uiStatusMapping: Map<string, string>
  viewForOptions: any[]
  selectedViewForOption: any

  isVisible: boolean = true
  taskTypes: TaskTypeAPIModel[]
  natureOfIssueOptionsList: any
  private statusMap: Map<string, string>
  readonly MODULE_NAME_OFFLINE_TRACKER = GlobalConstants.MODULE.OFFLINE_TRACKER

  constructor(
    private appState: Store<AppState>,
    private documentService: DocumentService,
    private cdr: ChangeDetectorRef,
    private readonly summaryTableService: SummaryTableService
  ) {
  }

  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    sortBy: 'createdDatetime',
    sortOrder: 'DESC'
  }

  searchRequestBody: any = {
    createdDatetimeRange: undefined
  }

  ngOnInit() {
    this.populateOptions()
    this.subscribeToAppState()
    this.offlineTrackerSummaryAPIData$ = this.appState.select(getSummaryList())
    this.summaryOptions = {
      downloadable: true,
      additionalButtons: true,
      contentSelectable: true,
      contentSelector: true,
      filterable: true,
      formattedContent: true,
      pageable: false,
      sortable: true,
      quickFilters: true
    }

    this.viewForOptions = this.summaryTableService.buildViewForOption()
    const firstViewForOption = this.viewForOptions[0].key
    this.selectedViewForOption = firstViewForOption
    this.searchRequestBody = { ...this.buildCreatedatetimeFilter(firstViewForOption) }

    this.summaryData = {
      content$: this.offlineTrackerSummaryAPIData$,
      contentSelector: {
        label: 'View for',
        selectedValue: this.selectedViewForOption,
        selectionOptions: this.viewForOptions,
        onChange: this.onViewForChange
      },
      headers: [
        ENTITY_ID_WITH_FILTER,
        OT_DISPLAY_ID,
        OT_EE_ID,
        OT_EE_NAME,
        OT_UNIQUE_MEMBER_ID,
        this.buildNatureOfIssueColumn(),
        OT_RECEIVED_DATE,
        this.buildCurrentStatusColumn(),
        LAST_UPDATED_BY,
        OT_LAST_UPDATED_ON
      ],
      additionalButtons: [
        {
          id: 'create-offline-tracker',
          label: 'CREATE NEW OFFLINE TRACKER',
          route: `/${GlobalConstants.ROUTING.OFFLINE_TRACKER}/${GlobalConstants.ROUTING.NEW}`
        }
      ],
      sortCallback: this.sortCallback,
      currentSearchParams: this.queryParamValues,
      searchCallback: this.searchCallback,
      downloadDocumentCallback: this.downloadDocumentCallback,
      checkBoxSelectionIds: [],
      selectPageCallback: this.selectPageCallback,
      onDropdownLimitChange: this.onDropdownLimitChange
    }

    this.appState
      .select('common')
      .subscribe(data => {
        const offlineTrackerSearchParams = data?.summaryTableData?.get(OFFLINE_TRACKER)
        if (offlineTrackerSearchParams) {
          const status = offlineTrackerSearchParams?.searchRequestBody?.statusList?.[0]
          const parentStatus = this.uiStatusMapping?.[status]
          let searchRequestBody = offlineTrackerSearchParams?.searchRequestBody
          if (parentStatus) {
            searchRequestBody = { ...searchRequestBody, statusList: parentStatus }
          }
          this.searchRequestBody = {
            ...this.searchRequestBody,
            createdDatetimeRange: offlineTrackerSearchParams.searchRequestBody?.createdDatetimeRange
          }
          const contentSelector = {
            ...this.summaryData.contentSelector,
            selectedValue: offlineTrackerSearchParams.selectedViewForOption
          }
          this.summaryData = { ...this.summaryData, appliedFilters: searchRequestBody, contentSelector }

          this.appState.dispatch(LOAD_OFFLINE_TRACKER_SUMMARY_DETAILS(offlineTrackerSearchParams))
        } else {
          this.loadSummaryData()
        }
      })
      .unsubscribe()
  }

  private loadSummaryData() {
    const options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(LOAD_OFFLINE_TRACKER_SUMMARY_DETAILS(options))
    this.appState.dispatch(
      SAVE_SUMMARY_TABLE_SEARCH_PARAMS({
        owner: OFFLINE_TRACKER,
        data: { ...options, selectedViewForOption: this.selectedViewForOption }
      })
    )
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  searchCallback = (searchParams, button, reset: boolean) => {
    let selectedStatus = []
    if (searchParams.statusList) {
      let selected = searchParams.statusList
      for (let [key, value] of Object.entries(this.uiStatusMapping)) {
        if (selected === value) {
          selectedStatus.push(key)
        }
      }
    }
    searchParams = this.filterEmptyProperties(searchParams)
    if (!Object.values(searchParams).some(value => value !== undefined && value !== '')) {
      searchParams = {}
    }
    this.queryParamValues.pageNo = 0
    this.searchRequestBody = {
      ...searchParams,
      statusList: selectedStatus,
      ...this.buildCreatedatetimeFilter(this.selectedViewForOption)
    }
    if (reset) {
      this.resetFilters()
    }
    this.loadSummaryData()
  }

  onViewForChange = $event => {
    if ($event.target.value != 'undefined') {
      this.selectedViewForOption = $event.target.value
      const searchRequestBody = {
        ...this.searchRequestBody,
        ...this.buildCreatedatetimeFilter($event.target.value)
      }
      this.searchRequestBody = searchRequestBody
      this.queryParamValues.sortBy = 'createdDatetime'
      this.queryParamValues.sortOrder = 'DESC'
    } else {
      const searchRequestBody = { ...this.searchRequestBody, createdDatetimeRange: undefined }
      this.searchRequestBody = searchRequestBody
      this.selectedViewForOption = null
    }
    this.loadSummaryData()
  }

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      }
    ]
  }

  downloadDocumentCallback = documentType => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const endPoint = `${
      ServiceUrlConstants.API_OFFLINE_TRACKER_SUMMARY_LIST + ServiceUrlConstants.API_EXPORT + documentType
    }`

    const options = {
      queryParams: this.buildQueryParams(true),
      requestBody: this.searchRequestBody
    }

    this.documentService.downloadDocument(endPoint, options)
  }

  selectPageCallback = pageNum => {
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  filterEmptyProperties(obj: any): any {
    const filteredObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== '') {
        filteredObj[key] = obj[key]
      }
    }
    return filteredObj
  }

  dateSelection() {
    if (this.fromDate && this.toDate) {
      this.searchRequestBody = {
        ...this.searchRequestBody,
        fromDate: this.fromDate,
        toDate: this.toDate
      }
      this.loadSummaryData()
    }
    if (!this.fromDate && !this.toDate) {
      this.searchRequestBody = {
        ...this.searchRequestBody,
        fromDate: null,
        toDate: null
      }
      this.loadSummaryData()
    }
  }

  buildCreatedatetimeFilter(value: string) {
    if (!value) {
      return null
    }
    const daterange = value.split('^')
    return { createdDatetimeRange: { begin: new Date(daterange[0]), end: new Date(daterange[1]) } }
  }

  resetFilters() {
    this.isVisible = false
    const firstViewForOption = this.viewForOptions[0].key
    this.selectedViewForOption = firstViewForOption
    this.searchRequestBody = {
      ...this.searchRequestBody,
      ...this.summaryTableService.buildEmptyCreatedatetimeFilterIfValueIsNull(firstViewForOption)
    }
    this.queryParamValues = {
      pageNo: 0,
      pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
      sortBy: 'createdDatetime',
      sortOrder: 'DESC'
    }
    this.fromDate = null
    this.toDate = null
    this.summaryData.contentSelector.selectedValue = this.selectedViewForOption
    this.cdr.detectChanges()
    this.isVisible = true
  }

  private buildCurrentStatusColumn(): Column {
    return {
      name: 'Current Status',
      key: 'status',
      type: ColumnType.STRING,
      cssClasses: ['rowColor'],
      filter: {
        key: 'status',
        type: FieldType.SELECT,
        getOptionsList: () => {
          return this.statusMap
        }
      },
      build: (data: any) => data?.['status']
    }
  }

  private buildNatureOfIssueColumn(): Column {
    return {
      name: 'Nature of Issue',
      key: 'natureOfIssue',
      type: ColumnType.STRING,
      build: (data: any) => this.getCommaSeparatedNatureOfIssue(data?.['natureOfIssue'])
    }
  }

  populateOptions() {
    this.appState.select(getOptionsFromState).subscribe(options => {
      if (options) {
        this.natureOfIssueOptionsList = options[GlobalConstants.OPTION_TYPE.NATURE_OF_ISSUE]
      }
    })
  }

  getCommaSeparatedNatureOfIssue(data: string[]): string {
    const matchingOptions = this.natureOfIssueOptionsList?.filter?.(option => data?.includes(option.key2))
    return matchingOptions?.map?.(option => option.key1).join(', ')
  }

  private subscribeToAppState(): void {
    this.appState
      .select(getStatusMapFromTaskTypes(this.MODULE_NAME_OFFLINE_TRACKER))
      .subscribe(statusMap => {
        if (statusMap) {
          this.statusMap = statusMap
        }
      })
    this.appState.select(getUIStatusMappingFromState).subscribe(uiStatusMapping => {
      if (uiStatusMapping) {
        this.uiStatusMapping = uiStatusMapping
      }
    })
  }
}
